import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/shared/api';
import {
  type CallWithDetails,
  type QAFeedback,
  type QAComment,
  type QAFeedbackList,
  type QAFeedbackListQueryParams,
  type QAEntityType,
} from '@intone/shared';

import { convertParamsToString } from '@/shared/lib/queryParams';

/**
 * Hook to fetch all QA feedbacks for summary stats 7 last days
 */
export function useQAFeedbackSummary(entityType?: QAEntityType) {
  const apiClient = useApiClient();
  const params = entityType ? `?lastDays=7&entity_type=${entityType}` : '?lastDays=7';

  return useQuery({
    queryKey: ['feedbacks', '7-days', entityType],
    queryFn: async () => {
      return await apiClient<{feedbacks: QAFeedbackList[], totalCount: number}>(`/qa-feedback/summary${params}`);
    },
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to fetch QA feedbacks list with filtering, sorting, and pagination
 */
export function useQAFeedbackList(params: QAFeedbackListQueryParams) {
  const apiClient = useApiClient();
  const queryString = convertParamsToString<QAFeedbackListQueryParams>(params);

  return useQuery({
    queryKey: ['feedbacks', params],
    queryFn: async () => {
      return await apiClient<{feedbacks: QAFeedbackList[], totalCount: number}>(`/qa-feedback?${queryString}`);
    },
    staleTime: 60 * 1000,
  });
}


/**
 * Hook to fetch QA feedback for an entity (call or conversation)
 */
export function useQAFeedback(entityId: string, entityType: QAEntityType = 'call'){
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['qa-feedback', entityId, entityType],
    queryFn: async () => {
      return await apiClient<QAFeedback>(`/qa-feedback/${entityId}?entity_type=${entityType}`);
    },
    placeholderData: () => {
      if (entityType === 'call') {
        return queryClient.getQueryData<CallWithDetails>(['call', entityId])?.qa_feedback || undefined
      }
      return undefined;
    },
    enabled: !!entityId,
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to create QA feedback
 */
export function useCreateQAFeedback() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      data: {
        entity_id: string;
        entity_type: QAEntityType;
        feedback: QAFeedback;
        comment: QAComment;
      }) => {
      return apiClient<{ feedback: QAFeedback, newComment: QAComment }>('/qa-feedback', {
        method: 'POST',
        body: JSON.stringify(data),
      })
    },
    onMutate: async (updatedItem) => {
      const queryKey = ['qa-feedback', updatedItem.entity_id, updatedItem.entity_type];
      const queryKeyComments = ['qa-comments', updatedItem.entity_id, updatedItem.entity_type];

      await queryClient.cancelQueries({ queryKey });
      await queryClient.cancelQueries({ queryKey: queryKeyComments });

      const previousFeedback = queryClient.getQueryData(queryKey);
      const previousComments = queryClient.getQueryData(queryKeyComments);

      queryClient.setQueryData(queryKey, () => ({
        ...updatedItem.feedback,
      }));

      queryClient.setQueryData(queryKeyComments, (old: QAComment[] = []) => [
        ...old,
        {
          ...updatedItem.comment,
        },
      ]);

      return { previousFeedback, previousComments, queryKey, queryKeyComments };
    },
    onSuccess: (realUpdatedItem, _, context) => {
      queryClient.setQueryData(context.queryKey, realUpdatedItem.feedback);

      queryClient.setQueryData(context.queryKeyComments, (old:QAComment[] = []) =>
        old.map(comment => comment.id === 1 ? realUpdatedItem.newComment : comment)
      );

      queryClient.setQueriesData(
        { queryKey: ['feedbacks'] },
        (oldData: any) => {
          if (!oldData) return oldData;

          const newFeedback = realUpdatedItem.feedback;

          const feedbacks = oldData.feedbacks.map((feedback: QAFeedbackList) =>
            feedback.id === newFeedback.entity_id
              ? { ...feedback, status: newFeedback.status, category: newFeedback.category }
              : feedback
          );
          return {
            ...oldData,
            feedbacks
          }
        }
      );
    },
    onError: (_, __, context) => {
      if (context) {
        queryClient.setQueryData(context.queryKey, context.previousFeedback);
        queryClient.setQueryData(context.queryKeyComments, context.previousComments);
      }
    },
  });
}

/**
 * Hook to update QA feedback
 */
export function useUpdateQAFeedback() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { entity_id: string; entity_type: QAEntityType; status: string; category: string; }) => {
      return apiClient<QAFeedback>('/qa-feedback', {
        method: 'PUT',
        body: JSON.stringify(data),
      })
    },
    onMutate: async (updatedItem) => {
      const queryKey = ['qa-feedback', updatedItem.entity_id, updatedItem.entity_type];
      await queryClient.cancelQueries({ queryKey });

      const previousList = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old: QAFeedback) => ({ ...old, ...updatedItem}));

      return { previousList, queryKey };
    },
    onSuccess: (realUpdatedItem, _, context) => {
      queryClient.setQueryData(
        context.queryKey,
        (old: QAFeedback) => old.entity_id === realUpdatedItem.entity_id ? realUpdatedItem : old
      );

      queryClient.setQueriesData(
        { queryKey: ['feedbacks'] },
        (oldData: any) => {
          if (!oldData) return oldData;

          const feedbacks = oldData.feedbacks.map((feedback: QAFeedbackList) =>
            feedback.id === realUpdatedItem.entity_id
              ? { ...feedback, status: realUpdatedItem.status, category: realUpdatedItem.category }
              : feedback
          );
          return {
            ...oldData,
            feedbacks
          }
        }
      );
    },
    onError: (_, __, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(context.queryKey, context.previousList);
      }
    },
  });
}
